import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  DOWNLINE_CHANGE_COMMISSION_TYPE_LABELS,
  DOWNLINE_CHANGE_CONTRACT_TYPE_LABELS,
  carrierPlanLabel,
  type DownlineChangeRequestCarrier,
} from "@aam/shared";
import { getSessionToken } from "../hooks/useSession";
import { Button } from "./ui/Button";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface Person {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

interface QueueRow {
  id: string;
  status: string;
  createdAt: string;
  notes: string | null;
  subjectUserId: string | null;
  formData: { carriers?: DownlineChangeRequestCarrier[] } | null;
  subjectPortalUser: Person;
  requestedByPortalUser: Person;
}

function personLabel(person: Person) {
  const name = [person.firstName, person.lastName].filter(Boolean).join(" ").trim();
  return name || person.email;
}

function CarrierSummary({ carrier }: { carrier: DownlineChangeRequestCarrier }) {
  const changedItems = carrier.items.filter((item) => item.newUplineName || item.newLevel);
  return (
    <div className="rounded-lg border border-border-subtle bg-surface-overlay p-3 text-xs">
      <p className="font-semibold text-content-primary">
        {carrierPlanLabel(carrier.carrier)}
        {carrier.contractNumber ? ` · ${carrier.contractNumber}` : ""}
      </p>
      <p className="mt-1 text-content-secondary">
        Contract type: {DOWNLINE_CHANGE_CONTRACT_TYPE_LABELS[carrier.contractType]} · Commission:{" "}
        {DOWNLINE_CHANGE_COMMISSION_TYPE_LABELS[carrier.commissionType]}
      </p>
      {changedItems.length > 0 && (
        <ul className="mt-2 space-y-1 text-content-secondary">
          {changedItems.map((item) => (
            <li key={item.contractId}>
              <span className="font-medium text-content-primary">{item.plan}</span>
              {item.newUplineName && (
                <>
                  {" "}
                  · upline {item.currentUplineName ?? "—"} → {item.newUplineName}
                </>
              )}
              {item.newLevel && (
                <>
                  {" "}
                  · level {item.currentLevel ?? "—"} → {item.newLevel}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Staff review of change requests uplines submitted for their downlines. */
export function DownlineChangeRequestQueue() {
  const token = getSessionToken();
  const [requests, setRequests] = useState<QueueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [resolveNotes, setResolveNotes] = useState<Record<string, string>>({});

  useEffect(() => {
    void loadQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function loadQueue() {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/downline-change-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to load change requests");
      setRequests(json.data as QueueRow[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load change requests");
    } finally {
      setLoading(false);
    }
  }

  async function resolveRequest(id: string, status: "APPROVED" | "REJECTED") {
    if (!token) return;
    setResolvingId(id);
    try {
      const res = await fetch(`${API_BASE}/api/downline-change-requests/${id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status, notes: resolveNotes[id]?.trim() || undefined }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to update request");
      await loadQueue();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update request");
    } finally {
      setResolvingId(null);
    }
  }

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3 border-t border-border-subtle pt-6">
        <div>
          <h2 className="font-display text-lg font-semibold text-content-primary">
            Downline change requests
          </h2>
          <p className="mt-1 text-sm text-content-secondary">
            Contract type, commission type, hierarchy, and level changes uplines asked for on behalf
            of their downlines.
          </p>
        </div>
        <Button size="sm" variant="secondary" onClick={() => void loadQueue()}>
          Refresh
        </Button>
      </div>

      {loading && (
        <div role="status" aria-busy="true" className="rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-6 text-sm text-content-muted">
          Loading change requests…
        </div>
      )}
      {error && (
        <div role="alert" className="rounded-lg border border-status-rejected-border bg-status-rejected-bg p-4 text-sm text-status-rejected-text">
          {error}
        </div>
      )}

      {!loading && requests.length === 0 && (
        <div className="rounded-[var(--card-radius)] border border-dashed border-border-default bg-surface-raised p-10 text-center">
          <p className="font-medium text-content-primary">Queue clear</p>
          <p className="mt-1 text-sm text-content-muted">No submitted downline change requests.</p>
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-2">
        {requests.map((request) => (
          <article key={request.id} className="space-y-4 rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-5 shadow-card">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-content-primary">
                  Change request for{" "}
                  {request.subjectUserId ? (
                    <Link to={`/dashboard/agents/${request.subjectUserId}`} className="text-accent hover:opacity-80">
                      {personLabel(request.subjectPortalUser)}
                    </Link>
                  ) : (
                    personLabel(request.subjectPortalUser)
                  )}
                </p>
                <p className="mt-1 text-xs text-content-secondary">
                  Requested by {personLabel(request.requestedByPortalUser)}
                </p>
                <p className="text-xs text-content-muted">{new Date(request.createdAt).toLocaleString()}</p>
              </div>
              <span className="rounded-md border border-status-pending-border bg-status-pending-bg px-2.5 py-1 text-xs font-semibold text-status-pending-text">
                {request.status}
              </span>
            </div>

            <div className="space-y-2">
              {(request.formData?.carriers ?? []).map((carrier) => (
                <CarrierSummary key={`${carrier.carrier}-${carrier.contractId}`} carrier={carrier} />
              ))}
            </div>

            <textarea
              value={resolveNotes[request.id] ?? ""}
              onChange={(e) => setResolveNotes((current) => ({ ...current, [request.id]: e.target.value }))}
              placeholder="Resolution notes (optional)"
              rows={2}
              className="focus-ring w-full rounded-lg border border-border-default bg-surface-overlay px-3 py-2 text-sm text-content-primary placeholder:text-content-muted"
            />

            <div className="flex flex-wrap gap-2 border-t border-border-subtle pt-4">
              <Button
                size="sm"
                disabled={resolvingId === request.id}
                onClick={() => void resolveRequest(request.id, "APPROVED")}
              >
                Approve
              </Button>
              <Button
                size="sm"
                variant="danger"
                disabled={resolvingId === request.id}
                onClick={() => void resolveRequest(request.id, "REJECTED")}
              >
                Reject
              </Button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
